import React, {
  ElementType,
  MouseEvent,
  MouseEventHandler,
  useState
} from 'react'

import Button from 'components_new/atoms/Button'
import Icon, { IconName } from 'components_new/atoms/Icon'
import IconButton, { IconButtonProps } from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import ButtonField from 'components_new/molecules/ButtonField'

import { Color } from 'themes'

import OptionsMenu, { OptionProps, OptionsMenuProps } from '../OptionsMenu'

interface OptionsMenuButtonProps {
  buttonComponent?: ElementType<any>
  ButtonProps?: object
  className?: string
  color?: Color | 'default' | 'disabled' | 'inherit'
  disabled?: boolean
  edge?: IconButtonProps['edge']
  iconName?: IconName
  MenuProps?: {
    maxHeight?: OptionsMenuProps['maxHeight']
    sx?: OptionsMenuProps['sx']
  }
  onClick?: MouseEventHandler<HTMLElement>
  options?: OptionProps[]
  size?: 'xsmall' | 'small' | 'medium'
  sx?: object
  tooltip?: string
  variant?: 'button' | 'buttonField' | 'icon'
}

/**
 * The OptionMenuButton component is used for
 * displaying one or more options (Icon
 * + Text) in a list when clicking a
 * variant of a button.
 */

const OptionsMenuButton = React.forwardRef(
  (props: OptionsMenuButtonProps, ref: any) => {
    const { variant = 'icon' } = props

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()

      if (props.onClick) {
        props.onClick(event)
      } else {
        setAnchorEl(event.currentTarget)
      }
    }

    const handleClose = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      setAnchorEl(null)
    }

    return (
      <>
        {variant === 'icon' ? (
          <Tooltip title={props.tooltip ?? ''}>
            <IconButton
              className={props.className}
              color={props.color ?? 'inherit'}
              component={props.buttonComponent}
              disabled={props.disabled}
              edge={props.edge}
              onClick={handleClick}
              onMouseDown={(event) => event.stopPropagation()}
              ref={ref}
              size={props.size === 'xsmall' ? 'small' : props.size}
              sx={props.sx}
            >
              <Icon
                fontSize={props.size === 'xsmall' ? undefined : props.size}
                name={props.iconName ?? 'MoreHorizOutlined'}
                sx={props.size === 'xsmall' ? { fontSize: 12 } : undefined}
              />
            </IconButton>
          </Tooltip>
        ) : variant === 'button' ? (
          <Button
            {...props.ButtonProps}
            className={props.className}
            disabled={props.disabled}
            onClick={handleClick}
            onMouseDown={(event) => event.stopPropagation()}
            ref={ref}
            sx={props.sx}
          >
            {props.tooltip}
          </Button>
        ) : (
          <ButtonField
            className={props.className}
            onClick={handleClick}
            onMouseDown={(event) => event.stopPropagation()}
            sx={props.sx}
            title={props.tooltip ?? ''}
          />
        )}

        {props.options ? (
          <OptionsMenu
            anchorEl={anchorEl}
            disableScrollLock={true}
            maxHeight={props.MenuProps?.maxHeight}
            onClose={handleClose}
            open={open}
            options={props.options}
            sx={props.MenuProps?.sx}
          />
        ) : null}
      </>
    )
  }
)

OptionsMenuButton.displayName = 'OptionsMenuButton'
export default OptionsMenuButton
